import { Currency, KYCLevel, MangopayPersonType } from "@daytrip/legacy-enums";
import { Exclude, Expose } from "class-transformer";
import { IsDefined, IsString, IsUUID } from "class-validator";
import { v4 as uuid } from "uuid";

import { GObjectType, GField } from "../graphql";
import { transformCurrencyToCurrencyISO } from "../transformers/transformCurrencyToCurrencyISO";
import { TransformToDate } from "../transformers/transformToDate";

import type { MangopayBankAccountInformation } from "./MangopayBankAccountInformation";
import type { MangopayWalletInformation } from "./MangopayWalletInformation";


@GObjectType("MangopayInformation")
@Exclude()
export class MangopayInformation {
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public _id: string = uuid();

    @GField()
    @IsString()
    @IsDefined()
    @Expose()
    public mangopayId: string;

    @IsDefined()
    @Expose()
    public wallets: MangopayWalletInformation[] = [];

    @IsDefined()
    @Expose()
    public bankAccounts: MangopayBankAccountInformation[] = [];

    @TransformToDate
    @Expose()
    public approvedAt?: Date;

    @IsDefined()
    @Expose()
    public personType?: MangopayPersonType;

    @IsString()
    @Expose()
    public identityProofDocumentId?: string;

    @IsString()
    @Expose()
    public articlesOfAssociationDocumentId?: string;

    @IsString()
    @Expose()
    public registrationProofDocumentId?: string;

    @IsString()
    @Expose()
    public shareholderDeclarationDocumentId?: string;

    @TransformToDate
    @Expose()
    public disabledAt?: Date;

    @IsUUID("4")
    @Expose()
    public billingInformationId?: string;

    @Expose()
    public KYCLevel: KYCLevel | string;

    public getWallet(currency = Currency.Euro) {
        return this.wallets.find((w) => w.currency === currency);
    }

    public getWalletOrFail(currency = Currency.Euro) {
        const wallet = this.getWallet(currency);
        if (!wallet) {
            throw Error(`Wallet not found for the given currency ${transformCurrencyToCurrencyISO(currency)}`);
        }
        return wallet;
    }

    public getBankAccount(currency = Currency.Euro) {
        return this.bankAccounts.find((w) => w.currency === currency);
    }

    public getBankAccountOrFail(currency = Currency.Euro) {
        const bankAccount = this.getBankAccount(currency);
        if (!bankAccount) {
            throw Error(`Bank account not found for the given currency ${transformCurrencyToCurrencyISO(currency)}`);
        }
        return bankAccount;
    }

    public upsertBankAccount(bankAccount: MangopayBankAccountInformation) {
        const existingIndex = this.bankAccounts.findIndex((a) => a.currency === bankAccount.currency);
        if (existingIndex > -1) {
            this.bankAccounts.splice(existingIndex, 1, bankAccount);
        } else {
            this.bankAccounts.push(bankAccount);
        }
    }

    public isKYCValid(): boolean {
        return !!(this.KYCLevel && this.KYCLevel === KYCLevel.Regular);
    }
}
